/* ============================================================
   <cart-drawer> custom element. Dawn-style.
   Listens for cart:updated, re-fetches its own section HTML,
   swaps innards. Binds qty/remove buttons to /cart/change.js.
   ============================================================ */

(function () {
  'use strict';

  if (window.customElements && window.customElements.get('cart-drawer')) return;

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onCartUpdated = this._onCartUpdated.bind(this);
      this._onQtyChange = this._onQtyChange.bind(this);
      this._onQtyKeydown = this._onQtyKeydown.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onQtyChange);
      this.addEventListener('keydown', this._onQtyKeydown);
      if (window.Moonrise && window.Moonrise.cart) {
        this._unsub = window.Moonrise.cart.subscribe('cart:updated', this._onCartUpdated);
      }
      this._bindOpenTriggers();
      this._bindClose();
    }

    _bindOpenTriggers() {
      var self = this;
      var triggers = document.querySelectorAll('.util-cart, [data-drawer-trigger="cart"]');
      triggers.forEach(function (t) {
        if (t.dataset.cartBound) return;
        t.dataset.cartBound = '1';
        t.addEventListener('click', function (e) {
          e.preventDefault();
          self._lastTrigger = e.currentTarget || e.target;
          self.open();
        });
      });
    }

    _bindClose() {
      var self = this;
      var closeBtn = this.querySelector('#cartDrawerClose, .cart-drawer-close');
      var backdrop = this.querySelector('#cartBackdrop, .drawer-backdrop');
      if (closeBtn && !closeBtn.dataset.cartBound) {
        closeBtn.dataset.cartBound = '1';
        closeBtn.addEventListener('click', function () { self.close(); });
      }
      if (backdrop && !backdrop.dataset.cartBound) {
        backdrop.dataset.cartBound = '1';
        backdrop.addEventListener('click', function () { self.close(); });
      }
      if (!document._cartEscBound) {
        document._cartEscBound = true;
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.isOpen()) self.close();
        });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('change', this._onQtyChange);
      this.removeEventListener('keydown', this._onQtyKeydown);
      if (this._unsub) this._unsub();
    }

    /* Typed quantity: applied on blur / Enter. Shopify checks stock; see
       _changeLine for the "only N available" message. */
    _onQtyChange(event) {
      var input = event.target.closest('[data-cart-qty-input]');
      if (!input) return;
      var row = input.closest('.cart-item');
      var line = parseInt(input.getAttribute('data-cart-line'), 10);
      var quantity = parseInt(input.value, 10);
      if (isNaN(line) || isNaN(quantity) || quantity < 0) {
        input.value = input.defaultValue;
        return;
      }
      if (String(quantity) === input.defaultValue) return;
      this._changeLine(line, quantity, row);
    }

    /* Enter in the quantity box must not submit the drawer form, whose
       default button is Checkout. */
    _onQtyKeydown(event) {
      if (event.key !== 'Enter') return;
      var input = event.target.closest('[data-cart-qty-input]');
      if (!input) return;
      event.preventDefault();
      input.blur();
    }

    _changeLine(line, quantity, row) {
      var self = this;
      var key = row ? row.getAttribute('data-key') : null;
      this._pending = { key: key, requested: quantity };
      this._setLoading(true);
      if (row) row.classList.add('is-updating');

      // Success: cart-store publishes cart:updated -> _onCartUpdated re-renders.
      window.Moonrise.cart.change(line, quantity)
        .catch(function (err) {
          self._pending = null;
          self._setLoading(false);
          if (row) {
            row.classList.remove('is-updating');
            var input = row.querySelector('[data-cart-qty-input]');
            if (input) input.value = input.defaultValue;
          }
          self._showLineMessage(key, (err && (err.description || err.message)) || 'We couldn’t update this item. Please try again.');
        });
    }

    _showLineMessage(key, text) {
      if (!key || !text) return;
      var rows = this.querySelectorAll('.cart-item[data-key]');
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].getAttribute('data-key') !== key) continue;
        var info = rows[i].querySelector('.cart-item-info');
        if (!info) return;
        var msg = info.querySelector('.cart-item-error');
        if (!msg) {
          msg = document.createElement('p');
          msg.className = 'cart-item-error';
          msg.setAttribute('role', 'alert');
          info.appendChild(msg);
        }
        msg.textContent = text;
        return;
      }
    }

    get sectionId() {
      return this.getAttribute('data-section-id');
    }

    _onClick(event) {
      // Close button / backdrop — delegated so it survives innerHTML swaps
      // after each cart:updated re-render.
      var closeEl = event.target.closest(
        '#cartDrawerClose, .cart-drawer-close, [data-cart-close]'
      );
      if (closeEl) {
        event.preventDefault();
        this.close();
        return;
      }
      var backdropEl = event.target.closest('#cartBackdrop, .drawer-backdrop');
      if (backdropEl) {
        event.preventDefault();
        this.close();
        return;
      }

      // Cart line quantity adjust / remove buttons
      var btn = event.target.closest('[data-cart-line][data-cart-quantity]');
      if (!btn) return;
      event.preventDefault();
      var line = parseInt(btn.getAttribute('data-cart-line'), 10);
      var quantity = parseInt(btn.getAttribute('data-cart-quantity'), 10);
      if (isNaN(line)) return;

      this._changeLine(line, quantity, btn.closest('.cart-item'));
    }

    _onCartUpdated(cart) {
      var id = this.sectionId;
      if (!id) return;
      var pending = this._pending;
      this._pending = null;
      window.Moonrise.cart.sectionRender(id, 'cart-drawer[data-section-id="' + id + '"]')
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var fresh = doc.querySelector('cart-drawer[data-section-id="' + id + '"]');
          if (!fresh) return;

          // Preserve open state across swap
          var wasOpen = this.querySelector('.cart-drawer.is-open');
          var backdropWasVisible = this.querySelector('.drawer-backdrop.is-visible');

          this.innerHTML = fresh.innerHTML;

          var newDrawer = this.querySelector('.cart-drawer');
          if (wasOpen) {
            if (newDrawer) newDrawer.classList.add('is-open');
            /* The swapped-in markup ships with inert (closed state). The
               drawer is open, so clear it and re-arm the focus trap on the
               fresh node, otherwise the contents drop out of the tab order
               mid-interaction. */
            if (newDrawer && window.Moonrise && window.Moonrise.overlay) {
              window.Moonrise.overlay.open(newDrawer, this._lastTrigger, null);
            }
          } else if (newDrawer) {
            newDrawer.setAttribute('inert', '');
          }
          if (backdropWasVisible) {
            var newBackdrop = this.querySelector('.drawer-backdrop');
            if (newBackdrop) newBackdrop.classList.add('is-visible');
          }
          this._setLoading(false);

          // Shopify caps a quantity at what's in stock; say so when it did.
          if (pending && pending.key && pending.requested > 0 && cart && cart.items) {
            var item = cart.items.filter(function (it) { return it.key === pending.key; })[0];
            var actual = item ? item.quantity : 0;
            if (actual < pending.requested) {
              this._showLineMessage(pending.key, actual > 0
                ? 'Only ' + actual + ' available.'
                : 'This item is no longer available.');
            }
          }

          // Re-bind close button + backdrop after innerHTML swap. The host
          // <cart-drawer>'s delegated click handler (_onClick) already
          // covers close + backdrop clicks for the swapped-in elements;
          // this is belt-and-suspenders for any other listeners we add.
          this._bindClose();
        }.bind(this))
        .catch(function () {
          this._setLoading(false);
          this.querySelectorAll('.cart-item.is-updating').forEach(function (r) { r.classList.remove('is-updating'); });
        }.bind(this));

      // Update header cart count badge live
      var countEl = document.querySelector('[data-cart-count]');
      if (countEl) {
        countEl.textContent = cart.item_count;
        countEl.setAttribute('aria-label', cart.item_count + ' items');
      }
    }

    _setLoading(isLoading) {
      this.classList.toggle('is-loading', !!isLoading);
    }

    open() {
      var d = this.querySelector('.cart-drawer');
      var b = this.querySelector('.drawer-backdrop');
      if (d) d.classList.add('is-open');
      if (b) b.classList.add('is-visible');
      document.body.classList.add('drawer-open');
      /* Shared helper owns inert, focus move, focus return and the Tab trap. */
      if (window.Moonrise && window.Moonrise.overlay) {
        window.Moonrise.overlay.open(d, this._lastTrigger, '#cartDrawerClose, .cart-drawer-close');
      }
    }

    close() {
      var d = this.querySelector('.cart-drawer');
      var b = this.querySelector('.drawer-backdrop');
      if (d) d.classList.remove('is-open');
      if (b) b.classList.remove('is-visible');
      document.body.classList.remove('drawer-open');
      if (window.Moonrise && window.Moonrise.overlay) {
        window.Moonrise.overlay.close(d);
      }
      this._lastTrigger = null;
    }

    isOpen() {
      var d = this.querySelector('.cart-drawer');
      return !!(d && d.classList.contains('is-open'));
    }
  }

  window.customElements.define('cart-drawer', CartDrawer);

  // Helper anyone can call to open the cart drawer from outside (e.g. after add-to-cart)
  if (window.Moonrise) {
    window.Moonrise.openCartDrawer = function () {
      var el = document.querySelector('cart-drawer');
      if (el && typeof el.open === 'function') el.open();
    };
  }
})();
